import React, { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import { bookingAPI } from '../lib/api';
import Layout from '../components/Layout';
import { Card, CardContent, CardHeader, CardTitle } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Badge } from '../components/ui/badge';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '../components/ui/tabs';
import { toast } from 'sonner';
import { Calendar, Clock, MapPin, Banknote, Loader2, XCircle, Building2 } from 'lucide-react';

export default function MyBookings() {
  const [loading, setLoading] = useState(true);
  const [bookings, setBookings] = useState([]);
  const [activeTab, setActiveTab] = useState('all');

  useEffect(() => {
    loadBookings();
  }, []);

  const loadBookings = async () => {
    try {
      const response = await bookingAPI.getMy();
      setBookings(response.data);
    } catch (error) {
      toast.error('Fehler beim Laden der Buchungen');
    } finally {
      setLoading(false);
    }
  };

  const handleCancel = async (bookingId) => {
    if (!window.confirm('Buchung wirklich stornieren? Der Betrag wird erstattet.')) return;
    
    try {
      await bookingAPI.cancel(bookingId);
      toast.success('Buchung storniert - Betrag wurde erstattet');
      loadBookings();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Fehler beim Stornieren');
    }
  };

  const statusBadge = (status) => {
    const styles = {
      pending: 'bg-yellow-100 text-yellow-800',
      confirmed: 'bg-green-100 text-green-800',
      completed: 'bg-blue-100 text-blue-800',
      rejected: 'bg-red-100 text-red-800',
      cancelled: 'bg-gray-100 text-gray-800'
    };
    const labels = {
      pending: 'Ausstehend',
      confirmed: 'Bestätigt',
      completed: 'Abgeschlossen',
      rejected: 'Abgelehnt',
      cancelled: 'Storniert'
    };
    return <Badge className={styles[status]}>{labels[status]}</Badge>;
  };

  const filteredBookings = activeTab === 'all' 
    ? bookings 
    : bookings.filter(b => b.status === activeTab);

  if (loading) {
    return (
      <Layout>
        <div className="flex items-center justify-center min-h-[400px]">
          <Loader2 className="w-8 h-8 animate-spin text-tunis-teal" />
        </div>
      </Layout>
    );
  }

  return (
    <Layout>
      <div className="space-y-6">
        {/* Header */}
        <div className="flex items-center justify-between">
          <div>
            <h1 className="text-2xl font-bold text-tunis-blue">Meine Buchungen</h1>
            <p className="text-muted-foreground">{bookings.length} Buchungen insgesamt</p>
          </div>
          <Link to="/fields">
            <Button className="btn-primary">Neuen Platz buchen</Button>
          </Link>
        </div>

        {/* Tabs */}
        <Tabs value={activeTab} onValueChange={setActiveTab}>
          <TabsList>
            <TabsTrigger value="all">Alle</TabsTrigger>
            <TabsTrigger value="pending">Ausstehend</TabsTrigger>
            <TabsTrigger value="confirmed">Bestätigt</TabsTrigger>
            <TabsTrigger value="completed">Abgeschlossen</TabsTrigger>
          </TabsList>
        </Tabs>

        {/* Bookings List */}
        {filteredBookings.length > 0 ? (
          <div className="space-y-4">
            {filteredBookings.map(booking => (
              <Card key={booking.id} className={`border-l-4 ${
                booking.status === 'pending' ? 'border-l-yellow-500' :
                booking.status === 'confirmed' ? 'border-l-green-500' :
                booking.status === 'completed' ? 'border-l-blue-500' :
                'border-l-gray-300'
              }`}>
                <CardContent className="pt-6">
                  <div className="flex flex-col md:flex-row md:items-center md:justify-between gap-4">
                    <div className="flex-1">
                      <div className="flex items-center gap-3 mb-2">
                        <h3 className="font-bold text-lg">{booking.field_name}</h3>
                        {statusBadge(booking.status)}
                      </div>
                      
                      <div className="grid grid-cols-2 md:grid-cols-4 gap-3 text-sm">
                        <div className="flex items-center gap-2 text-muted-foreground">
                          <Calendar className="w-4 h-4" />
                          <span>{new Date(booking.date).toLocaleDateString('de-DE')}</span>
                        </div>
                        <div className="flex items-center gap-2 text-muted-foreground">
                          <Clock className="w-4 h-4" />
                          <span>{booking.start_time} - {booking.end_time}</span>
                        </div>
                        <div className="flex items-center gap-2 text-muted-foreground">
                          <Banknote className="w-4 h-4" />
                          <span className="font-semibold text-tunis-teal">{(booking.total_price || 0).toFixed(2)} TND</span>
                        </div>
                      </div>
                      
                      {booking.notes && (
                        <p className="text-sm text-muted-foreground mt-2">Notiz: {booking.notes}</p>
                      )}
                    </div>
                    
                    {booking.status === 'pending' && (
                      <Button
                        variant="destructive"
                        size="sm"
                        onClick={() => handleCancel(booking.id)}
                      >
                        <XCircle className="w-4 h-4 mr-1" /> Stornieren
                      </Button>
                    )}
                  </div>
                  
                  {booking.status === 'pending' && (
                    <p className="text-sm text-yellow-600 mt-3 bg-yellow-50 p-2 rounded">
                      ⏳ Warte auf Bestätigung des Feldbesitzers
                    </p>
                  )}
                  {booking.status === 'confirmed' && (
                    <p className="text-sm text-green-600 mt-3 bg-green-50 p-2 rounded">
                      ✅ Bestätigt! Du kannst spielen.
                    </p>
                  )}
                  {booking.status === 'rejected' && (
                    <p className="text-sm text-red-600 mt-3 bg-red-50 p-2 rounded">
                      ❌ Abgelehnt. Der Betrag wurde erstattet.
                    </p>
                  )}
                </CardContent>
              </Card>
            ))}
          </div>
        ) : (
          <Card>
            <CardContent className="py-16 text-center">
              <Building2 className="w-16 h-16 mx-auto text-gray-300 mb-4" />
              <h3 className="text-lg font-semibold mb-2">Keine Buchungen</h3>
              <p className="text-muted-foreground mb-4">
                {activeTab === 'all' 
                  ? 'Du hast noch keine Buchungen vorgenommen'
                  : `Keine ${activeTab === 'pending' ? 'ausstehenden' : activeTab === 'confirmed' ? 'bestätigten' : 'abgeschlossenen'} Buchungen`}
              </p>
              <Link to="/fields">
                <Button className="btn-primary">Jetzt Platz buchen</Button>
              </Link>
            </CardContent>
          </Card>
        )}
      </div>
    </Layout>
  );
}
